<script lang="ts">
  import { params, goto } from "@roxi/routify";
  import { omitBy, clone } from "lodash";

  // services
  import {
    getQuickOrderListIndex,
    getQuickOrderListProducts,
    prepareFilters,
  } from "@shared/services/list_service";
  import { getDistributors } from "@modern/services/distributors";
  import { getListBanners } from "@shared/services/banner_service";
  import { getProductTypes } from "@modern/services/product_types";
  import { getStateSettings } from "@shared/services/state_settings_service";
  import retailerContext from "@modern/services/retailer_context";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { shouldHideAllDistributorInfo } from "@shared/services/retailer_user_authentication_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  // components
  import ListIndex from "@retailer/svelte/Shared/QuickOrderList/ListIndex.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";

  // stores
  import QuickOrderListProductsStore from "@retailer/svelte/stores/QuickOrderListProductsStore";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";

  // types
  import { ListType } from "@shared/types/QuickOrderList";
  import type { Distributor } from "@shared/types/Distributor";
  import type { PageData } from "@shared/types/PageData";
  import type { QuickOrderListFilters, QuickOrderListIndex } from "@shared/types/QuickOrderList";
  import type { StateSetting } from "@shared/types/StateSetting";
  import type {
    Category,
    Container,
    ProductType,
    QuickOrderListProduct,
  } from "@shared/types/ProductLine";
  import type { RetailerType } from "@shared/types/Retailer";
  import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";

  type LoadData = {
    distributors: Distributor[];
    fulfillmentMethods?: Record<number, RetailerFulfillmentMethod>;
    lists?: QuickOrderListIndex[];
    productTypes: {
      categories: Category[];
      containers: Container[];
      types: ProductType[];
    };
    quickOrderListProducts?: QuickOrderListProduct[];
    smartFilterDistributors?: Distributor[];
    stateSettings?: StateSetting;
  };

  let data: LoadData;
  let filters: QuickOrderListFilters;
  let page: PageData;

  const authService = getControlStateAuthenticationService();

  const filter = async (event: CustomEvent): Promise<void> => {
    let tempStateParams = clone($params);

    if (event) tempStateParams = Object.assign(tempStateParams, event.detail.filters);

    $goto(
      "/lists",
      omitBy(tempStateParams, param => param === null || param === undefined || param === "null"),
    );
  };

  const getDistributorsFilteredByParams = (
    distributors: Distributor[],
    params: Record<string, any>,
  ): Distributor[] => {
    if (params.smart_filter_distributor) {
      return distributors.filter(distributor => distributor.id == params.smart_filter_distributor);
    } else {
      return distributors;
    }
  };

  const getPageData = (params: Record<string, any>): PageData => {
    return {
      current: "layout.quick_order_list_index",
      params,
      sortOrder: null,
    };
  };

  const getParamDependentData = (
    params: Record<string, any>,
  ): [Promise<LoadData["productTypes"]>, Promise<QuickOrderListProduct[]>] => {
    const defaultSort = { sort: "product_line_name", direction: "ASC" };

    return [
      getProductTypes({
        filtered: true,
        smart_filter_distributor: params.smart_filter_distributor,
      }),
      getQuickOrderListProducts({
        page: params.page || 1,
        ...defaultSort,
        ...params,
      }),
    ];
  };

  async function load(params: Record<string, any>): Promise<LoadData> {
    const [
      lists,
      smartFilterDistributors,
      stateSettings,
      _fulfillmentMethods,
      productTypes,
      quickOrderListProducts,
    ] = await Promise.all([
      getQuickOrderListIndex(),
      getDistributors({
        filtered: true,
        with_integrated: true,
      }),
      getStateSettings(),
      RetailerFulfillmentMethodsStore.refreshFulfillmentMethods(),
      ...getParamDependentData(params),
    ]);

    const distributors = getDistributorsFilteredByParams(smartFilterDistributors, params);

    QuickOrderListProductsStore.set(quickOrderListProducts);
    StateSettingsStore.set(stateSettings);
    page = getPageData(params);
    filters = prepareFilters({ distributors, productTypes, smartFilterDistributors });

    await getListBanners(
      ListType.MySavedProducts,
      filters,
      params,
      quickOrderListProducts?.length || 0,
    );

    return {
      distributors,
      lists,
      productTypes,
      smartFilterDistributors,
    };
  }

  async function reload(params: Record<string, any>): Promise<LoadData> {
    const { smartFilterDistributors } = data;
    const [productTypes, quickOrderListProducts] = await Promise.all(getParamDependentData(params));
    const distributors = getDistributorsFilteredByParams(smartFilterDistributors, params);

    QuickOrderListProductsStore.set(quickOrderListProducts);
    page = getPageData(params);
    filters = prepareFilters({ distributors, productTypes, smartFilterDistributors });
    getListBanners(ListType.MySavedProducts, filters, params, quickOrderListProducts?.length || 0);

    return {
      distributors,
      productTypes,
    };
  }

  async function fetchData(params: Record<string, any>): Promise<void> {
    if (data) data = Object.assign(data, await reload(params));
    else data = await load(params);
  }

  $: fetchData(clone($params));

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  $: {
    if (hasRetailerUserAuthRollout && filters && filters.distributors) {
      let filteredDistributors = filters.distributors.filter(
        d => !shouldHideAllDistributorInfo($RetailerUserAuthenticationsStore, [d.id]),
      );
      filters.distributors = filteredDistributors;
    }
  }
</script>

{#if data && data.lists && $params}
  <ListIndex
    {filter}
    {filters}
    {page}
    lists={data.lists}
    smartFilterDistributor={$params.smart_filter_distributor}
  />
{/if}

{#if authService?.authenticationModalComponent}
  <svelte:component this={authService?.authenticationModalComponent} />
{/if}
